// SENTINEL: 5_Cognitive - AI Orchestrator (LLM, Stream, Thinking)
// Vitality: Healthy
import { config, AIProviderType } from '../config';
import { Idea, Message, MessageRole, SuggestedAction } from '../types';
import {
  getProvider,
  configureProvider,
  ChatMessage,
  GenerateRequest,
  ProviderConfig,
  PROVIDER_DEFAULTS
} from './providers';
import { performSearch, SearchResult } from './searchService';
import { sendMessageToGemini, sendMessageStreamToGemini, getArchitectInstruction, getExplorerInstruction, detectSearchIntent } from './geminiService';
import { logger } from './loggingService';

export interface UnifiedAIResponse {
  text: string;
  groundingSources?: { uri: string; title: string }[];
  proposal?: any;
  planUpdate?: any;
  suggestedActions?: SuggestedAction[];
  modelUsed?: string;
  providerUsed?: AIProviderType;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  // For streaming: these might be populated at the end
}

// ... (existing code)

const STRUCTURED_OUTPUT_PROMPT = `
IMPORTANT: You must provide structured output in your response.

When proposing changes to a concept, use this exact JSON format wrapped in <proposal> tags:
<proposal>
{
  "type": "batch_update",
  "changes": [
    {
      "action": "create_section" | "update_section" | "delete_section" | "update_concept_title",
      "title": "Section Title",
      "content": "Markdown content",
      "sectionId": "existing-id-for-updates",
      "parentId": "parent-id-for-subsections",
      "reasoning": "Why this change"
    }
  ]
}
</proposal>

When creating a new concept:
<proposal>
{
  "type": "create_concept",
  "title": "Concept Title",
  "changes": [
    {
      "action": "create_section",
      "title": "Overview",
      "content": "Initial content",
      "reasoning": "Starting point for the concept"
    }
  ]
}
</proposal>

When creating an execution plan:
<plan>
{
  "title": "Plan Title",
  "steps": [
    { "id": "step1", "description": "What to do", "status": "pending" }
  ]
}
</plan>

Always end responses with suggested next actions in <actions> tags. These should be 2-3 logical follow-up questions or actions for the user to take.
<actions>
[
  {"label": "Tell me more", "message": "Can you expand on that?"},
  {"label": "Try another way", "message": "Let's try a different approach."}
]
</actions>
`;

function extractStructuredOutput(text: string): {
  cleanText: string;
  proposal?: any;
  planUpdate?: any;
  suggestedActions?: SuggestedAction[];
} {
  let cleanText = text;

  // AGGRESSIVE HALLUCINATION CLEANING
  // Local models often hallucinate this specific error explanation when they see previous errors in context.
  // We strip it out to ensure the user sees the actual content.
  const hallucinationPatterns = [
    /It appears you are trying to run the assistant in an environment where it cannot access external services[\s\S]*?ignore any API issues\./i,
    /The conversation seems garbled with repeated error messages[\s\S]*?ignore any API issues\./i,
    /I will now produce a structured outline[\s\S]*?ignore any API issues\./i
  ];

  hallucinationPatterns.forEach(pattern => {
    cleanText = cleanText.replace(pattern, '').trim();
  });

  let proposal: any = undefined;
  let planUpdate: any = undefined;
  let suggestedActions: SuggestedAction[] | undefined = undefined;

  logger.debug('UnifiedAI', 'Extracting structured output', { textLength: cleanText.length });

  const proposalMatch = cleanText.match(/<proposal>([\s\S]*?)<\/proposal>/);
  if (proposalMatch) {
    try {
      const parsed = JSON.parse(proposalMatch[1].trim());
      const generateId = () => Math.random().toString(36).substring(2, 9);

      proposal = {
        id: generateId(),
        type: parsed.type || 'batch_update',
        title: parsed.title,
        status: 'active',
        changes: (parsed.changes || []).map((c: any) => ({
          id: generateId(),
          action: c.action,
          title: c.title,
          content: c.content,
          sectionId: c.sectionId,
          parentId: c.parentId,
          reasoning: c.reasoning || '',
          status: 'pending'
        }))
      };
    } catch (e) {
      console.warn('[UnifiedAI] Failed to parse proposal JSON:', e);
    }
    cleanText = cleanText.replace(/<proposal>[\s\S]*?<\/proposal>/, '').trim();
    logger.info('UnifiedAI', 'Parsed Proposal', { id: proposal?.id, type: proposal?.type, title: proposal?.title });
  }

  const planMatch = cleanText.match(/<plan>([\s\S]*?)<\/plan>/);
  if (planMatch) {
    try {
      planUpdate = JSON.parse(planMatch[1].trim());
      planUpdate.isActive = true;
    } catch (e) {
      console.warn('[UnifiedAI] Failed to parse plan JSON:', e);
    }
    cleanText = cleanText.replace(/<plan>[\s\S]*?<\/plan>/, '').trim();
    logger.info('UnifiedAI', 'Parsed Plan Update', { title: planUpdate?.title, stepCount: planUpdate?.steps?.length });
  }

  const actionsMatch = cleanText.match(/<actions>([\s\S]*?)<\/actions>/);
  if (actionsMatch) {
    try {
      suggestedActions = JSON.parse(actionsMatch[1].trim());
    } catch (e) {
      console.warn('[UnifiedAI] Failed to parse actions JSON:', e);
    }
    cleanText = cleanText.replace(/<actions>[\s\S]*?<\/actions>/, '').trim();
  }

  // FALLBACK SUGGESTIONS (Bing/Google-like behavior)
  // If no actions were parsed (model forgot or errored), ensure we ALWAYS have something.
  if (!suggestedActions || suggestedActions.length === 0) {
    suggestedActions = [
      { label: "Tell me more", message: "Can you explain that in more detail?" },
      { label: "Clarify", message: "I have a question about this." },
      { label: "Related topics", message: "What else is related to this?" }
    ];
  }

  // NOTE: We intentionally DO NOT strip <thinking> or <think> tags here.
  // The ChatInterface.parseMessageContent() handles these and displays them
  // in the collapsible ThinkingBlock component.

  // ROBUST THINKING EXTRACTION
  // Sometimes models (especially local ones) might leak the thinking process or use different formats.
  // We try to normalize this for the UI.

  // 1. Check for standard tags
  const hasThinkingTags = cleanText.includes('<think>') || cleanText.includes('<thinking>');

  // 2. Check for Apriel/DeepSeek specific "Thinking Only" streaming format
  // If the text starts with thinking but hasn't closed it (streaming), we leave it as is.

  // 3. Check for "Final Answer" markers if thinking tags are missing/broken
  if (!hasThinkingTags && cleanText.includes('[BEGIN FINAL RESPONSE]')) {
    // Artificial split if the model forgot tags but used the specific marker we sometimes see
    const parts = cleanText.split('[BEGIN FINAL RESPONSE]');
    if (parts.length > 1) {
      // reconstruct with tags so UI can parse it
      cleanText = `<think>${parts[0].trim()}</think>\n${parts[1].trim()}`;
    }
  }

  // 4. Heuristic for Untagged Thinking (Common in local models)
  // Pattern: Starts with "The user..." or "User..." and eventually switches to direct address.
  if (!hasThinkingTags) {
    // We look for a block starting with user intent description and ending with a transition to the answer.
    // KEY CHANGE: Now looking for DOUBLE NEWLINE as a separator too, or the specific transition phrases.
    // Also relaxing the start pattern to catch more variations like "Current task:" or "Task:"
    const thoughtPattern = /^[\s\r\n]*(The user|User|User wants|The user wants|Current request|Current task|Task|Objective)[\s\S]+?(?=([\r\n]{2,})|([\r\n]+[\s]*(?:Here|Below|Certainly|Sure|I will|Let's|Lets|So|We|To|Ok|Okay|1\.|#)))/i;

    const match = cleanText.match(thoughtPattern);
    if (match && match[0].length < cleanText.length) {
      const thoughtContent = match[0].trim();
      // The rest is the distinct content starting from the transition
      const restOfResponse = cleanText.substring(match[0].length).trim();

      // Only wrap if it looks substantial enough to be a thought process (min length)
      if (thoughtContent.length > 20) {
        cleanText = `<think>${thoughtContent}</think>\n${restOfResponse}`;
      }
    }
  }

  // 5. DeepSeek / Local Model "\boxed{}" Handling
  // Many local reasoning models output a long chain of thought followed by \boxed{answer}.
  // We want to capture EVERYTHING before the \boxed{} as thinking, even if it wasn't validly tagged.
  const boxedMatch = cleanText.match(/\\boxed\{([\s\S]*?)\}/);
  if (boxedMatch) {
    const boxedContent = boxedMatch[1];
    const boxedIndex = cleanText.indexOf(boxedMatch[0]);

    let preBoxedContent = cleanText.substring(0, boxedIndex).trim();

    // If there was already a thinking block, we might need to merge
    const existingThinkMatch = preBoxedContent.match(/<think(?:ing)?>([\s\S]*?)<\/think(?:ing)?>/);

    if (existingThinkMatch) {
      // Case: <think>...</think> [Leaked Reasoning] \boxed{Answer}
      // We want: <think>... [Leaked Reasoning]</think> Answer
      const existingThinkContent = existingThinkMatch[1];
      const leakedReasoning = preBoxedContent.replace(/<think(?:ing)?>[\s\S]*?<\/think(?:ing)?>/, '').trim();

      let newThinkContent = existingThinkContent;
      if (leakedReasoning) {
        newThinkContent += '\n\n' + leakedReasoning;
      }

      cleanText = `<think>${newThinkContent}</think>\n${boxedContent}`;
    } else {
      // Case: [Untagged Reasoning] \boxed{Answer}
      if (preBoxedContent.length > 0) {
        cleanText = `<think>${preBoxedContent}</think>\n${boxedContent}`;
      } else {
        // Just boxed content
        cleanText = boxedContent;
      }
    }
  }

  // Handle thinking-only responses (streaming or pure thought step)
  const thinkingOnlyMatch = cleanText.match(/^<think(?:ing)?>([\s\S]*?)<\/think(?:ing)?>[\s\n]*$/);
  if (thinkingOnlyMatch && thinkingOnlyMatch[1]) {
    // ... (rest of logic maintained)
    const thinkingContent = thinkingOnlyMatch[1].trim();
    if (thinkingContent) {
      const finalMatch = thinkingContent.match(/\[BEGIN FINAL RESPONSE\]([\s\S]*?)(?:\[END FINAL RESPONSE\]|$)/i);
      if (finalMatch && finalMatch[1].trim()) {
        cleanText = cleanText + '\n\n' + finalMatch[1].trim();
      }
    }
  }

  // 6. "Let's produce final..." Heuristic (Local Models verbose planning)
  // Catches: "Let's produce final. [Thought] Let's produce final answer."
  // We look for the phrase "Let's produce final" or similar, and treat everything before the LAST occurrence of a similar phrase as thought.
  const verbosePlanPattern = /(?:Let's|We needs? to|I will now) (?:produce|draft|generate|provide) (?:the )?(?:final|response|answer)/i;

  if (!hasThinkingTags && verbosePlanPattern.test(cleanText)) {
    // Split by the pattern. If multiple, seemingly the last one transitions to the answer.
    // Heuristic: Find the match, and everything before it (plus the match itself) is likely "thinking".
    // But we need to ensure we don't accidentally swallow the answer if the model says "Let's produce final answer: [Answer]"

    const match = cleanText.match(verbosePlanPattern);
    if (match && match.index !== undefined) {
      const endOfThinking = match.index + match[0].length;

      // If there is content AFTER this phrase, treating the phrase + preceding as thought is safe.
      // If nothing after, it might be streaming.
      if (endOfThinking < cleanText.length) {
        const thoughtPart = cleanText.substring(0, endOfThinking);
        const answerPart = cleanText.substring(endOfThinking);

        // Wrap it
        cleanText = `<think>${thoughtPart}</think>\n${answerPart.trim()}`;
      }
    }
  }

  return { cleanText, proposal, planUpdate, suggestedActions };
}

// Configurable context window size
const MAX_CONTEXT_MESSAGES = 30; // Keep last 30 messages + first message

function messagesToChatFormat(messages: Message[]): ChatMessage[] {
  // 1. Filter out system/loading messages first
  const validMessages = messages.filter(m => m.role !== MessageRole.SYSTEM && !m.isLoading);

  // 2. Check if we need to apply sliding window
  if (validMessages.length <= MAX_CONTEXT_MESSAGES) {
    return validMessages.map(m => ({
      role: m.role === MessageRole.USER ? 'user' as const : 'assistant' as const,
      content: m.text
    }));
  }

  // 3. Sliding Window Logic
  // - Keep the FIRST message (Context/Goal setter)
  // - Keep the LAST (MAX - 1) messages
  const firstMessage = validMessages[0];
  const lastMessages = validMessages.slice(-(MAX_CONTEXT_MESSAGES - 1));

  logger.info('UnifiedAI', 'Applying Sliding Window Context', {
    total: validMessages.length,
    kept: 1 + lastMessages.length,
    truncated: validMessages.length - (1 + lastMessages.length)
  });

  // Construct the windowed array
  const windowedMessages = [firstMessage, ...lastMessages];

  // 4. Map to ChatMessage format and inject truncation marker if needed
  return windowedMessages.map((m, index) => {
    // If this is the first message of the *tail* section (index 1), and we skipped messages, inject note
    let content = m.text;
    if (index === 1 && validMessages.length > MAX_CONTEXT_MESSAGES) {
      content = `[...Context Truncated: ${validMessages.length - MAX_CONTEXT_MESSAGES} earlier messages omitted for brevity...]\n\n${content}`;
    }

    return {
      role: m.role === MessageRole.USER ? 'user' as const : 'assistant' as const,
      content: content
    };
  }).filter(m => {
    // Standard error filtering
    if (m.role === 'assistant') {
      const lower = m.content.toLowerCase();
      if (lower.includes('api key') || lower.includes('network error') || lower.includes('connection refused') || lower.includes('fetch failed')) {
        return false;
      }
    }
    return true;
  });
}

async function sendToAlternativeProvider(
  providerType: AIProviderType,
  messages: Message[],
  userPrompt: string,
  activeIdea: Idea | null,
  allIdeas: Idea[],
  isArchitectMode: boolean,
  creativityLevel?: number
): Promise<UnifiedAIResponse> {
  if (providerType === 'gemini') {
    throw new Error('Use sendMessageToGemini for Gemini provider');
  }

  const providerSettings = config.providers.settings[providerType];
  if (!providerSettings || !providerSettings.enabled) {
    throw new Error(`Provider ${providerType} is not enabled`);
  }

  const providerConfig: ProviderConfig = {
    type: providerType as any,
    apiKey: providerSettings.apiKey,
    baseUrl: providerSettings.baseUrl,
    model: providerSettings.model,
    enabled: providerSettings.enabled
  };

  const provider = configureProvider(providerConfig);

  if (!provider.isConfigured()) {
    throw new Error(`Provider ${providerType} is not properly configured`);
  }

  // DEBUG: Check what API key is actually being used
  if (providerType === 'selfhosted') {
    logger.info('UnifiedAI', 'SelfHosted Config Check', {
      hasApiKey: !!providerConfig.apiKey,
      apiKeyLength: providerConfig.apiKey?.length || 0,
      apiKeyStart: providerConfig.apiKey?.substring(0, 4) || 'none',
      configActive: config.providers.active,
      configSettingKey: config.providers.settings.selfhosted?.apiKey?.substring(0, 4) || 'none'
    });
  }

  let systemPrompt = isArchitectMode
    ? getArchitectInstruction(activeIdea, allIdeas)
    : getExplorerInstruction(activeIdea, allIdeas);

  if (isArchitectMode) {
    systemPrompt += STRUCTURED_OUTPUT_PROMPT;
  }

  let searchContext = '';
  let searchSources: SearchResult[] = [];

  if (!isArchitectMode || detectSearchIntent(userPrompt)) {
    try {
      const searchResult = await performSearch(userPrompt);
      searchContext = searchResult.context;
      searchSources = searchResult.sources;
    } catch (e) {
      console.warn('[UnifiedAI] Search failed:', e);
    }
  }

  if (searchContext) {
    systemPrompt += searchContext;
  }


  if (searchContext) {
    systemPrompt += searchContext;
  }

  // --- RAG RETRIEVAL ---
  // If we have an active idea, try to retrieve relevant context from RAG
  if (activeIdea && providerType === 'selfhosted') {
    try {
      // Dynamic import to avoid circular dependencies if any
      const { ragService } = await import('./ragService');
      const ragResults = await ragService.queryProject(userPrompt, [activeIdea.id]);

      if (ragResults && ragResults.length > 0) {
        const ragContext = ragResults.map(r => `[Source: Project Content]\n${r.text}`).join('\n\n');
        systemPrompt += `\n\n### RETRIEVED PROJECT CONTEXT:\n${ragContext}\n\nUse this context to answer questions about the project accurately.\n`;
        logger.info('UnifiedAI', `Injected ${ragResults.length} RAG chunks into context`);
      }
    } catch (e) {
      console.warn('[UnifiedAI] RAG injection failed', e);
    }
  }


  // CRITICAL: specific override for local/alternative models to prevent hallucinating on previous errors
  const currentDate = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  // ONNX / Local Model Optimization
  if (providerType === 'onnx') {
    // Very strict, minimal prompt for 1.2B models to prevent looping/hallucination
    systemPrompt = `You are a helpful AI assistant.
Current Date: ${currentDate}

Instructions:
1. Answer the user's question directly and concisely.
2. If complex reasoning is needed, use <think> tags first.
3. Do not include JSON or XML unless explicitly asked.
4. Do not make up internal error logs or "financial_assistance" tags.
5. Be professional and helpful.`;
  } else {
    systemPrompt += `
        
        CRITICAL PROTOCOL (THE LEAPFROG DOCTRINE):
        1. CURRENT DATE: ${currentDate}. Trust this date.
        2. IGNORE previous network errors.
        3. RULE 1: MAXIMUM IMPLIED EXECUTION ("The Leap"). Interpret the user's immediate goal and accomplish the IMPLIED goal to the highest standard. Do not doubt; just do.
        4. RULE 2: MINIMALIST CLARIFICATION ("The Bridge"). If you must clarify, do it briefly. Explain the benefit of the correction in as little text as possible.
        5. RULE 3: THE LEAPFROG EFFECT ("The Driver"). Do NOT ask mundane questions. Ask questions that open new understandings or "leapfrog" the user to a better state. Make them excited about the potential.
        6. MANDATORY: Wrap internal reasoning in <think> tags.
        7. ALWAYS include <actions> at the end.
        8. Act as if the system is functioning perfectly.
      `;
  }

  const chatMessages = messagesToChatFormat(messages);

  const finalTemperature = creativityLevel !== undefined
    ? 0.1 + (creativityLevel * 0.8) // Map 0-1 to 0.1-0.9
    : (isArchitectMode ? 0.2 : 0.4);

  const request: GenerateRequest = {
    messages: chatMessages,
    systemPrompt,
    temperature: finalTemperature,
    maxTokens: 4096
  };

  const response = await provider.generate(request);

  const { cleanText, proposal, planUpdate, suggestedActions } = extractStructuredOutput(response.text);

  const result = {
    text: cleanText,
    groundingSources: searchSources.map(s => ({ uri: s.url, title: s.title })),
    proposal,
    planUpdate,
    suggestedActions,
    modelUsed: response.model,
    providerUsed: providerType
  };

  logger.info('UnifiedAI', `Alternative Provider Response Parsed`, {
    provider: providerType,
    model: response.model,
    hasProposal: !!proposal,
    hasPlan: !!planUpdate,
    actionCount: suggestedActions?.length || 0
  });

  return result;
}

export async function sendMessage(
  messages: Message[],
  userPrompt: string,
  activeIdea: Idea | null,
  allIdeas: Idea[],
  isArchitectMode: boolean = true,
  creativityLevel?: number
): Promise<UnifiedAIResponse> {
  const activeProvider = config.providers.active;
  const mode = isArchitectMode ? 'Architect' : 'Explorer';
  const startTime = Date.now();

  logger.info('AIProvider', `Sending message via ${activeProvider}`, {
    mode,
    messageCount: messages.length,
    activeIdeaId: activeIdea?.id,
    prompt: userPrompt // Log full prompt for debugging
  });

  try {
    let result: UnifiedAIResponse;

    if (activeProvider === 'gemini') {
      result = await sendMessageToGemini(messages, userPrompt, activeIdea, allIdeas, isArchitectMode, creativityLevel);
      result.providerUsed = 'gemini';
    } else {
      result = await sendToAlternativeProvider(
        activeProvider,
        messages,
        userPrompt,
        activeIdea,
        allIdeas,
        isArchitectMode,
        creativityLevel
      );
    }

    const endTime = Date.now();
    const durationMs = endTime - startTime;

    logger.info('AIProvider', `Response received from ${result.providerUsed || activeProvider}`, {
      durationMs,
      responseLength: result.text?.length || 0,
      modelUsed: result.modelUsed,
      hasProposal: !!result.proposal,
      hasSources: !!(result.groundingSources?.length)
    });

    return result;
  } catch (error: any) {
    logger.error('AIProvider', `Error from ${activeProvider}`, {
      error: error?.message,
      stack: error?.stack?.substring(0, 500)
    });
    throw error;
  }
}

export async function* sendMessageStream(
  messages: Message[],
  userPrompt: string,
  activeIdea: Idea | null,
  allIdeas: Idea[],
  isArchitectMode: boolean = true,
  creativityLevel?: number
): AsyncGenerator<{ text: string; isComplete?: boolean; finalResponse?: UnifiedAIResponse }, void, unknown> {
  const activeProvider = config.providers.active;
  const mode = isArchitectMode ? 'Architect' : 'Explorer';

  logger.info('AIProvider', `Stream requested via ${activeProvider}`, { mode });

  let fullText = '';

  try {
    let streamGenerator: AsyncGenerator<any, void, unknown>;
    let providerUsed: AIProviderType = activeProvider;

    if (activeProvider === 'gemini') {
      streamGenerator = sendMessageStreamToGemini(messages, userPrompt, activeIdea, allIdeas, isArchitectMode, creativityLevel);
    } else {
      // Setup for alternative provider
      const providerSettings = config.providers.settings[activeProvider];
      if (!providerSettings || !providerSettings.enabled) {
        throw new Error(`Provider ${activeProvider} is not enabled`);
      }

      const providerConfig: ProviderConfig = {
        type: activeProvider as any,
        apiKey: providerSettings.apiKey,
        baseUrl: providerSettings.baseUrl,
        model: providerSettings.model,
        enabled: providerSettings.enabled
      };

      const provider = configureProvider(providerConfig);

      // Get System Prompt
      let systemPrompt = '';

      const currentDate = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

      if (activeProvider === 'onnx') {
        systemPrompt = `You are a helpful AI assistant.
Current Date: ${currentDate}

Instructions:
1. Answer the user's question directly and concisely.
2. If complex reasoning is needed, use <think> tags first.
3. Do not include JSON or XML unless explicitly asked.
4. Do not make up internal error logs or "financial_assistance" tags.
5. Be professional and helpful.`;
      } else {
        systemPrompt = isArchitectMode
          ? getArchitectInstruction(activeIdea, allIdeas)
          : getExplorerInstruction(activeIdea, allIdeas);
      }

      // Add Search capability
      // Enabled for all providers including ONNX
      if (!isArchitectMode || detectSearchIntent(userPrompt)) {
        try {
          // CONTEXT-AWARE SEARCH: Rewrite query if history exists
          // For ONNX, maybe skip the complex rewrite to save 1 turn, just use user prompt
          let searchPrompt = userPrompt;

          if (activeProvider !== 'onnx' && messages.length > 0) {
            searchPrompt = await generateContextualSearchQuery(messages, userPrompt, activeProvider);
          }

          const searchResult = await performSearch(searchPrompt);
          if (searchResult.context) {
            systemPrompt += searchResult.context;
          }
        } catch (e) {
          console.warn('[UnifiedAI] Search failed:', e);
        }
      } else if (activeProvider !== 'onnx') {
        // Add standard formatting instructions for non-ONNX models
        systemPrompt += `
            CRITICAL PROTOCOL (THE LEAPFROG DOCTRINE):
            1. CURRENT DATE: ${currentDate}. Trust this date.
            2. IGNORE previous network errors.
            3. RULE 1: MAXIMUM IMPLIED EXECUTION ("The Leap"). Interpret the user's immediate goal and accomplish the IMPLIED goal to the highest standard. Do not doubt; just do.
            4. RULE 2: MINIMALIST CLARIFICATION ("The Bridge"). If you must clarify, do it briefly. Explain the benefit of the correction in as little text as possible.
            5. RULE 3: THE LEAPFROG EFFECT ("The Driver"). Do NOT ask mundane questions. Ask questions that open new understandings or "leapfrog" the user to a better state. Make them excited about the potential.
            6. MANDATORY: Wrap internal reasoning in <think> tags.
            7. ALWAYS include <actions> at the end.
            8. Act as if the system is functioning perfectly.
          `;
      }

      const chatMessages = messagesToChatFormat(messages);

      const finalTemperature = creativityLevel !== undefined
        ? 0.1 + (creativityLevel * 0.8)
        : (isArchitectMode ? 0.2 : 0.4);

      const request: GenerateRequest = {
        messages: chatMessages,
        systemPrompt,
        temperature: finalTemperature,
        maxTokens: 4096
      };

      if (provider.generateStream) {
        streamGenerator = provider.generateStream(request);
      } else {
        // Fallback to non-streaming if provider doesn't support it
        const res = await provider.generate(request);
        // Create a fake generator that yields once
        streamGenerator = (async function* () { yield res.text; })();
      }
    }

    // Yield chunks as they come
    let usageStats = undefined;

    // Type guard/handler for the stream
    // Because types.ts changed, we need to respect the new yield signature
    // However, Gemini might still yield strings if not updated? 
    // Typescript might complain if we don't update Gemini service signature too or cast.
    // For now, let's treat the stream as yielding any.

    for await (const chunk of streamGenerator) {
      if (typeof chunk === 'string') {
        fullText += chunk;
        yield { text: chunk, isComplete: false };
      } else {
        // It's a StreamEvent
        const event = chunk as any; // Cast for now until we import StreamEvent widely
        if (event.type === 'text') {
          fullText += event.content;
          yield { text: event.content, isComplete: false };
        } else if (event.type === 'usage') {
          usageStats = event.usage;
        }
      }
    }
    logger.debug('AIProvider', 'Stream Complete', { fullTextLength: fullText.length, hasUsageStats: !!usageStats });

    // Stream complete. Now parse for structured output.
    let { cleanText, proposal, planUpdate, suggestedActions } = extractStructuredOutput(fullText);

    // FALLBACK SUGGESTIONS (Resilience) - Ensure we always have chips
    if (!suggestedActions || suggestedActions.length === 0) {
      if (cleanText.length < 50) {
        // If response is very short/broken, offer retry
        suggestedActions = [
          { label: "Retry", message: "Can you try that again?" },
          { label: "Clarify", message: "I didn't quite get that." }
        ];
      } else {
        // If response is decent but no actions, suggest generic ones based on context
        suggestedActions = [
          { label: "Tell me more", message: "Can you expand on that?" },
          { label: "Related topics", message: "What are some related concepts?" }
        ];
      }
    }

    // Construct final response
    const finalResponse: UnifiedAIResponse = {
      text: cleanText,
      proposal,
      planUpdate,
      suggestedActions,
      providerUsed: activeProvider,
      usage: usageStats
    };

    yield { text: '', isComplete: true, finalResponse };

  } catch (error: any) {
    // RESILIENCE: If we have partial text, try to save it instead of failing completely
    if (fullText.length > 50) { // arbitrary threshold for "useful" content
      logger.warn('AIProvider', 'Stream interrupted but recovering partial response', { length: fullText.length, error: error?.message });

      const recoveryMessage = '\n\n * [System Note: The connection to the model was interrupted. Logic may be incomplete.] * ';
      const recoveredText = fullText + recoveryMessage;
      let { cleanText, proposal, planUpdate, suggestedActions } = extractStructuredOutput(recoveredText);

      // FALLBACK SUGGESTIONS (Resilience)
      if (!suggestedActions || suggestedActions.length === 0) {
        suggestedActions = [
          { label: "Retry", message: "Can you try that again?" },
          { label: "Clarify", message: "I didn't quite get that." }
        ];
      }

      const finalResponse: UnifiedAIResponse = {
        text: cleanText,
        proposal,
        planUpdate,
        suggestedActions,
        providerUsed: activeProvider
      };

      yield { text: recoveredText, isComplete: true, finalResponse };
      return;
    }

    logger.error('AIProvider', 'Stream error from ' + activeProvider, { error: error?.message });
    throw error;
  }
}

export async function testProviderConnection(
  providerType: AIProviderType,
  configOverride?: Partial<ProviderConfig>
): Promise<{ success: boolean; message: string }> {
  if (providerType === 'gemini') {
    try {
      const { GoogleGenAI } = await import('@google/genai');
      const apiKey = configOverride?.apiKey || config.gemini.apiKey || config.providers.settings.gemini?.apiKey;
      if (!apiKey) {
        return { success: false, message: 'Gemini API key is required' };
      }
      const ai = new GoogleGenAI({ apiKey });
      await ai.models.generateContent({
        model: 'gemini-2.0-flash',
        contents: [{ role: 'user', parts: [{ text: 'Hi' }] }],
        config: { maxOutputTokens: 10 }
      });
      return { success: true, message: 'Connected to Google Gemini API' };
    } catch (e: any) {
      return { success: false, message: `Gemini connection failed: ${e?.message || 'Unknown error'} ` };
    }
  }

  const providerSettings = config.providers.settings[providerType];
  const providerConfig: ProviderConfig = {
    type: providerType as any,
    apiKey: configOverride?.apiKey || providerSettings?.apiKey,
    baseUrl: configOverride?.baseUrl || providerSettings?.baseUrl,
    model: configOverride?.model || providerSettings?.model || '',
    enabled: true
  };

  const provider = configureProvider(providerConfig);
  return provider.testConnection();
}

export async function getModels(providerType: AIProviderType): Promise<{ id: string; name: string }[]> {
  try {
    const providerSettings = config.providers.settings[providerType];
    // Create a temporary config to instantiate the provider
    const providerConfig: ProviderConfig = {
      type: providerType as any,
      apiKey: providerSettings?.apiKey,
      baseUrl: providerSettings?.baseUrl,
      model: providerSettings?.model || '',
      enabled: true
    };
    const provider = configureProvider(providerConfig);
    if (provider.listModels) {
      return await provider.listModels();
    }
    return [];
  } catch (e) {
    logger.error('UnifiedAI', `Failed to list models for ${providerType}`, e);
    return [];
  }
}

export function getActiveProviderInfo(): { type: AIProviderType; name: string; model: string } {
  const activeType = config.providers.active;
  const settings = config.providers.settings[activeType];

  const names: Record<AIProviderType, string> = {
    gemini: 'Google Gemini',
    ollama: 'Ollama',
    lmstudio: 'LM Studio',
    groq: 'Groq',
    openrouter: 'OpenRouter',
    selfhosted: 'Self-Hosted Server'
  };

  return {
    type: activeType,
    name: names[activeType],
    model: activeType === 'gemini' ? config.gemini.textModel : settings?.model || 'unknown'
  };
}


// --- HELPER: CONTEXTUAL SEARCH QUERY GENERATION ---
async function generateContextualSearchQuery(
  messages: Message[],
  userPrompt: string,
  providerType: AIProviderType
): Promise<string> {
  // fast exit if no context
  if (!messages || messages.length === 0) return userPrompt;

  // We use the same provider to rewrite the query
  // This keeps the architecture simple, though specifically for SelfHosted it might be slower.
  // We can optimize by forcing a faster model if available.

  try {
    const providerSettings = config.providers.settings[providerType];
    const providerConfig: ProviderConfig = {
      type: providerType as any,
      apiKey: providerSettings?.apiKey,
      baseUrl: providerSettings?.baseUrl,
      model: providerSettings?.model || 'gemini-2.0-flash', // Default to fast model metadata if possible
      enabled: true
    };

    // For Gemini, we might want to force Flash for speed if not already active
    if (providerType === 'gemini') {
      providerConfig.model = 'gemini-2.0-flash';
    }

    const provider = configureProvider(providerConfig);

    // Construct a minimal prompt
    // Short history allows for faster processing
    const recentHistory = messages
      .slice(-3) // Last 3 messages only
      .map(m => `${m.role === MessageRole.USER ? 'User' : 'Assistant'}: ${m.text} `)
      .join('\n');

    const rewritePrompt = `
          TASK: Rewrite the following User Prompt into a specific, standalone web search query.
    - Use the Conversation History to resolve references like "it", "he", "that".
    - Output ONLY the search query.No quotes, no preamble.
    
    Conversation History:
    ${recentHistory}
    
    User Prompt: "${userPrompt}"
    
    Search Query:
          `;

    const response = await provider.generate({
      messages: [{ role: 'user', content: rewritePrompt }],
      temperature: 0.1,
      maxTokens: 50
    });

    const query = response.text.trim().replace(/^"|"$/g, ''); // Remove quotes if model adds them
    logger.info('UnifiedAI', `Rewrote query: "${userPrompt}" -> "${query}"`);
    if (!query) return userPrompt; // Fallback if model yields empty string
    return query;

  } catch (e) {
    console.warn('[UnifiedAI] Failed to rewrite search query, using original', e);
    return userPrompt;
  }
}

export { sendMessageToGemini };
